import {
  View,
  Text,
  ScrollView,
  Platform,
  useWindowDimensions,
} from "react-native";
import { BlurView } from "expo-blur";
import { LinearGradient } from "expo-linear-gradient";
import MaskedView from "@react-native-masked-view/masked-view";
import { Check } from "lucide-react-native";
import type { ContentBlockId, ArticleBody, ContentTag } from "@louise/content";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../onboarding/theme";
import { MoonPressable } from "@louise/moon-ui";

export type Article = {
  id: ContentBlockId;
  title: string;
  time: string;
  tag: string;
  teaser: string;
  body: ArticleBody;
  tags: ContentTag[];
};

type Props = {
  label?: string;
  articles: Article[];
  onOpenArticle?: (article: Article) => void;
  isRead: (id: ContentBlockId) => boolean;
  showProgress?: boolean;
  topSpacing?: number;
};

export function ArticleRailCard({
  article,
  read,
  onPress,
  action = "article_section_row",
  cardMaxWidth,
  fullWidth,
}: {
  article: Article;
  read: boolean;
  onPress: () => void;
  /** Stable analytics identifier for the rail this card belongs to. */
  action?: string;
  cardMaxWidth?: number;
  fullWidth?: boolean;
}) {
  return (
    <MoonPressable
      onPress={onPress}
      action={action}
      haptic="light"
      activeScale={0.96}
      style={
        {
          width: fullWidth ? "100%" : 180,
          maxWidth: fullWidth ? undefined : cardMaxWidth,
          height: fullWidth ? undefined : 220,
          minHeight: fullWidth ? 160 : undefined,
          borderRadius: 20,
          overflow: "hidden",
          backgroundColor: "rgba(255,255,255,0.25)",
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.6)",
          ...(Platform.OS === "web" && {
            backdropFilter: "blur(60px)",
            WebkitBackdropFilter: "blur(60px)",
          }),
        } as Record<string, unknown>
      }
    >
      {Platform.OS !== "web" && (
        <BlurView
          intensity={60}
          tint="light"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
      )}
      <View
        style={{
          flex: 1,
          padding: 18,
          justifyContent: "space-between",
        }}
      >
        <View className="flex-row items-center justify-between">
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                fontSize: 10,
                fontWeight: "600",
                color: EMBER.colors.textFaint,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              {article.tag}
            </Text>
            {article.time ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  color: EMBER.colors.textFaint,
                  marginTop: 2,
                }}
              >
                {article.time}
              </Text>
            ) : null}
          </View>
          {read && (
            <View
              style={{
                width: 20,
                height: 20,
                borderRadius: 10,
                backgroundColor: `${EMBER.colors.green}14`,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Check size={11} color={EMBER.colors.green} strokeWidth={2} />
            </View>
          )}
        </View>
        <View style={{ gap: 6 }}>
          <Text
            className="font-dm-sans"
            numberOfLines={3}
            style={{
              fontSize: 15,
              fontWeight: "700",
              color: EMBER.colors.text,
              lineHeight: 20,
            }}
          >
            {article.title}
          </Text>
          <Text
            className="font-dm-sans"
            numberOfLines={2}
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              lineHeight: 17,
            }}
          >
            {article.teaser}
          </Text>
        </View>
      </View>
    </MoonPressable>
  );
}

export default function ArticleSectionCard({
  label,
  articles,
  onOpenArticle,
  isRead,
  showProgress,
  topSpacing,
}: Props) {
  const t = useTranslations();
  const { width: screenWidth } = useWindowDimensions();
  const cardMaxWidth = screenWidth * 0.43;
  const readCount = articles.filter((a) => isRead(a.id)).length;

  return (
    <View style={{ marginTop: topSpacing ?? (label ? 20 : 12) }}>
      {label ? (
        <View className="mb-3 flex-row items-center justify-between px-1">
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: EMBER.colors.textFaint,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            {label}
          </Text>
          {showProgress && (
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: EMBER.colors.textFaint }}
            >
              {t("learn.section_progress", {
                params: {
                  read: String(readCount),
                  total: String(articles.length),
                },
              })}
            </Text>
          )}
        </View>
      ) : null}

      {/* Horizontal scrolling cards */}
      {(() => {
        const scrollContent = (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              gap: 12,
              paddingLeft: 16,
              paddingRight: 32,
            }}
          >
            {articles.map((article) => {
              const read = isRead(article.id);
              return (
                <ArticleRailCard
                  key={article.id}
                  article={article}
                  read={read}
                  onPress={() => onOpenArticle?.(article)}
                  cardMaxWidth={cardMaxWidth}
                />
              );
            })}
          </ScrollView>
        );

        return (
          <View style={{ marginHorizontal: -16 }}>
            {Platform.OS === "web" ? (
              <View
                style={
                  {
                    WebkitMaskImage:
                      "linear-gradient(to right, black 90%, transparent 100%)",
                    maskImage:
                      "linear-gradient(to right, black 90%, transparent 100%)",
                  } as Record<string, unknown>
                }
              >
                {scrollContent}
              </View>
            ) : (
              <MaskedView
                style={{ flex: 1 }}
                maskElement={
                  <LinearGradient
                    colors={["black", "black", "transparent"]}
                    locations={[0, 0.9, 1]}
                    start={{ x: 0, y: 0.5 }}
                    end={{ x: 1, y: 0.5 }}
                    style={{ flex: 1 }}
                  />
                }
              >
                {scrollContent}
              </MaskedView>
            )}
          </View>
        );
      })()}
    </View>
  );
}
